import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { api } from "../lib/apiClient";
import { Navbar } from "../components/Navbar";
import { VideoCard } from "../components/VideoCard";
import { Row } from "../components/Row";
import { ShortsFeed } from "../components/ShortsFeed";
import { SponsorRow } from "../components/SponsorRow";
import { LeadCaptureForm } from "../components/LeadCaptureForm";
import { ChannelAbout } from "../components/ChannelAbout";
import { usePageMeta, channelMeta } from "../lib/seo";
import { shareExternal } from "../lib/share";
import { Share2 } from "lucide-react";

const MS_PER_MINUTE = 60000;

function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.floor((Date.now() - then) / MS_PER_MINUTE);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

export default function ChannelPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/channels/${slug}`).then((r) => setData(r.data)).catch(() => {});
  }, [slug]);

  const videos = useMemo(() => data?.videos || [], [data]);
  usePageMeta(channelMeta(data?.channel, `/channel/${slug}`));
  const categories = useMemo(() => {
    const present = [...new Set(videos.map((v) => v.category))];
    if (data?.channel?.category_scheme === "music") {
      const order = ["Live Sets", "Music Videos", "Playlists"];
      return [
        ...order.filter((c) => present.includes(c)),
        ...present.filter((c) => !order.includes(c)),
      ];
    }
    if (data?.channel?.topic_options) {
      const order = data.channel.topic_options;
      return [...order.filter((c) => present.includes(c)), ...present.filter((c) => !order.includes(c))];
    }
    // Always surface the live services row first when present.
    if (present.includes("Live Services & Replays") || present.includes("Worship & Praise")) {
      const order = ["Live Services & Replays", "Worship & Praise"];
      return [
        ...order.filter((c) => present.includes(c)),
        ...present.filter((c) => !order.includes(c)),
      ];
    }
    return present;
  }, [videos, data]);
  const videosByCategory = useMemo(() => {
    const grouped = {};
    for (const cat of categories) grouped[cat] = videos.filter((v) => v.category === cat);
    return grouped;
  }, [categories, videos]);

  // Living Abroad: two-dimensional rows — by region, then by topic.
  const abroadSections = useMemo(() => {
    if (data?.channel?.category_scheme !== "livingabroad") return null;
    const buildRows = (key, order) => {
      const present = [...new Set(videos.map((v) => v[key]).filter(Boolean))];
      const ordered = [
        ...order.filter((c) => present.includes(c)),
        ...present.filter((c) => !order.includes(c)),
      ];
      return ordered.map((label) => ({ label, items: videos.filter((v) => v[key] === label) }));
    };
    return {
      regions: buildRows("region", ["Panama", "Dominican Republic", "Other Locations"]),
      topics: buildRows("topic", ["Real Estate", "Relocation Guides", "Expat Life"]),
    };
  }, [videos, data]);

  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030305]">
        <div className="w-10 h-10 border-2 border-[#FF2A2A] border-t-transparent rounded-full animate-spin" />
      </div>
    );

  const { channel } = data;
  const featured = videos.find((v) => v.is_live) || videos[0];

  if (channel.format === "shorts") {
    const sections = channel.slug === "qtvtiktok" ? ["Women's Fashion", "Music & Dance", "Funny", "Travel", "Fitness", "Automotive", "Outdoor Life", "Lifestyle", "Philadelphia", "Sports"] : [];
    return (
      <div className="min-h-screen bg-[#030305]" data-testid="shorts-channel">
        <Navbar />
        <ShortsFeed videos={videos} sections={sections} initialSection={searchParams.get("section") || "All"} />
        <div className="py-16">
          <ChannelAbout channel={channel} text={data.about} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030305]">
      <Navbar />

      {/* Channel hero */}
      <section className="relative h-[75vh] min-h-[520px] overflow-hidden">
        <img src={channel.image} alt={channel.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/60 to-[#030305]/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030305] via-[#030305]/40 to-transparent" />
        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            {featured?.is_live ? (
              <span data-testid="hero-live-badge" className="inline-flex items-center gap-1.5 bg-[#FF2A2A] text-white text-[11px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live Now
              </span>
            ) : (
              <p className="text-xs tracking-[0.3em] uppercase text-[#FF2A2A] mb-3">{channel.tagline}</p>
            )}
            <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tighter">{channel.name}</h1>
            <p className="mt-4 text-base text-zinc-300 max-w-xl leading-relaxed">{channel.description}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
            {featured && (
              <Link
                to={`/watch/${featured.id}`}
                data-testid="channel-play-featured"
                className="inline-flex items-center gap-2 bg-white hover:bg-white/90 text-black px-8 py-3.5 rounded-sm font-bold transition-colors"
              >
                <Play className="w-5 h-5 fill-black" /> {featured.is_live ? "Watch Live" : "Play"}
              </Link>
            )}
              <button
                data-testid="channel-share-btn"
                onClick={() => shareExternal(`${window.location.origin}/api/share/c/${channel.slug}`, channel.name)}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/25 text-white px-6 py-3.5 rounded-sm font-semibold transition-colors"
              >
                <Share2 className="w-5 h-5" /> Share
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* On-demand rows */}
      <div className="relative z-10 space-y-10 pt-8 pb-24">
        <SponsorRow slug={channel.slug} />
        {data.updated_at && (
          <div className="flex items-center gap-2 px-6 md:px-10" data-testid="channel-updated-at">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF2A2A]" />
            <span className="text-xs tracking-[0.15em] uppercase text-zinc-500">
              Videos updated {timeAgo(data.updated_at)} · auto-refreshes daily
            </span>
          </div>
        )}
        {abroadSections ? (
          <>
            <div className="px-6 md:px-10">
              <h2 className="font-display text-2xl uppercase tracking-tight text-white/90">By Region</h2>
            </div>
            {abroadSections.regions.map(({ label, items }) => (
              <Row key={`region-${label}`} title={label} testid={`channel-row-region-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                {items.map((v) => (
                  <div key={v.id} className="w-[220px] md:w-[300px] shrink-0">
                    <VideoCard video={v} />
                  </div>
                ))}
              </Row>
            ))}
            <div className="px-6 md:px-10 pt-4">
              <h2 className="font-display text-2xl uppercase tracking-tight text-white/90">By Topic</h2>
            </div>
            {abroadSections.topics.map(({ label, items }) => (
              <Row key={`topic-${label}`} title={label} testid={`channel-row-topic-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                {items.map((v) => (
                  <div key={v.id} className="w-[220px] md:w-[300px] shrink-0">
                    <VideoCard video={v} />
                  </div>
                ))}
              </Row>
            ))}
          </>
        ) : (
          categories.map((cat) => (
            <Row key={cat} title={cat} testid={`channel-row-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
              {videosByCategory[cat].map((v) => (
                <div key={v.id} className="w-[220px] md:w-[300px] shrink-0">
                  <VideoCard video={v} />
                </div>
              ))}
            </Row>
          ))
        )}

        <div className="pt-6">
          <ChannelAbout channel={channel} text={data.about} />
        </div>

        {["livingabroad", "rv", "business"].includes(channel.slug) && (
          <div className="pt-6">
            <LeadCaptureForm channel={channel.slug} />
          </div>
        )}
      </div>
    </div>
  );
}
